// ─────────────────────────────────────────────────────────────
// Market
// Extracted from App.js.
//
// Enhancements over original:
//   1. Live ticking countdown (replaces static minsUntil chip)
//   2. Medium-impact toggle on Today's Events + Full Week Calendar
//   3. Surprise Tracker card (actual vs forecast delta)
//   4. Impact Heatmap card (7-day risk density grid)
//   5. Pre-Market Brief — "Best windows" row is now dynamic,
//      built from actual high-impact event times today instead
//      of hardcoded 9:30–11:30 copy.
// ─────────────────────────────────────────────────────────────

import { useState, useEffect, useRef } from "react";
import { Card } from "../../components/ui/Card";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { MarketSentimentWidget } from "../../components/widgets/MarketSentimentWidget";
import { useCalendar } from "../../hooks/useCalendar";
import { C } from "../../lib/colors";
import { EventPerformancePanel } from "./EventPerformancePanel";

// ─── Helpers ──────────────────────────────────────────────────

function formatCountdown(ms) {
  if (ms <= 0) return "NOW";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m ${String(sec).padStart(2, "0")}s`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, "0")}s`;
  return `${sec}s`;
}

// Returns a signed delta string if both actual and forecast are numeric.
// e.g. actual "0.3", forecast "0.2" → "+0.1"
function getSurpriseDelta(ev) {
  const actual   = parseFloat(ev.actual);
  const forecast = parseFloat(ev.forecast);
  if (isNaN(actual) || isNaN(forecast) || ev.forecast === "—") return null;
  return actual - forecast;
}

function surpriseColor(delta) {
  if (delta > 0)  return C.green;
  if (delta < 0)  return C.red;
  return C.muted;
}

function fmtDelta(delta) {
  const sign = delta > 0 ? "+" : "";
  return `${sign}${delta.toFixed(2)}`;
}

// Given a list of high-impact events with real times, build volatility
// window strings like "8:15–9:15 AM ET" (–15 min / +30 min around event).
function buildVolatilityWindows(highEvents) {
  return highEvents
    .filter(ev => ev.dt && ev.time !== "All Day")
    .map(ev => {
      const base  = ev.dt.getTime();
      const start = new Date(base - 15 * 60000);
      const end   = new Date(base + 30 * 60000);
      const etOpts = { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" };
      const startStr = start.toLocaleTimeString("en-US", etOpts);
      const endStr   = end.toLocaleTimeString("en-US",   etOpts);
      return { label: `${startStr} – ${endStr} ET`, event: ev.event };
    });
}

// Build a 7-day heatmap from eventsByDay.
// Returns array of { label, dateLabel, high, medium, low, isToday }
function buildHeatmap(eventsByDay) {
  const now = new Date();
  const etOpts = { timeZone: "America/New_York", weekday: "short", month: "short", day: "numeric" };
  const todayLabel = now.toLocaleDateString("en-US", etOpts);

  // Build the next 7 day labels in ET so we can match them to eventsByDay keys
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(now.getTime() + i * 86400000);
    const label = d.toLocaleDateString("en-US", etOpts);
    const evs   = eventsByDay[label] || [];
    days.push({
      label,
      shortLabel: i === 0 ? "Today" : d.toLocaleDateString("en-US", { weekday: "short", timeZone: "America/New_York" }),
      dateNum: d.toLocaleDateString("en-US", { month: "numeric", day: "numeric", timeZone: "America/New_York" }),
      high:    evs.filter(e => e.impact === "high").length,
      medium:  evs.filter(e => e.impact === "medium").length,
      low:     evs.filter(e => e.impact === "low").length,
      isToday: label === todayLabel,
    });
  }
  return days;
}

function heatCellBg(day) {
  if (day.high >= 3)   return "rgba(255,23,68,0.20)";
  if (day.high >= 1)   return "rgba(255,23,68,0.09)";
  if (day.medium >= 2) return "rgba(255,214,0,0.15)";
  if (day.medium >= 1) return "rgba(255,214,0,0.07)";
  if (day.low > 0)     return "rgba(255,255,255,0.03)";
  return "transparent";
}

function heatCellBorder(day) {
  if (day.isToday)     return C.blue + "60";
  if (day.high >= 1)   return C.red  + "35";
  if (day.medium >= 1) return C.yellow + "30";
  return C.border + "30";
}

// ─── Sub-components ───────────────────────────────────────────

// 1. Live Countdown — ticks every second
function LiveCountdown({ nextHighImpact, color }) {
  const [, setTick] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    ref.current = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(ref.current);
  }, []);

  if (!nextHighImpact) return null;

  const ms = nextHighImpact.dt.getTime() - Date.now();

  return (
    <div style={{
      background: color + "15",
      border: `1px solid ${color}30`,
      borderRadius: 8,
      padding: "6px 14px",
      textAlign: "center",
      minWidth: 90,
    }}>
      <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
        Next Event
      </div>
      <div style={{
        fontSize: 15,
        fontWeight: 700,
        color,
        fontFamily: "monospace",
        fontVariantNumeric: "tabular-nums",
        letterSpacing: "0.02em",
      }}>
        {formatCountdown(ms)}
      </div>
    </div>
  );
}

// 2. Medium-impact toggle button
function MediumToggle({ show, onToggle }) {
  return (
    <button
      onClick={onToggle}
      style={{
        background: show ? C.yellow + "18" : "none",
        border: `1px solid ${show ? C.yellow + "50" : C.border}`,
        borderRadius: 8,
        padding: "5px 12px",
        color: show ? C.yellow : C.sub,
        cursor: "pointer",
        fontSize: 11,
        fontWeight: show ? 700 : 400,
        transition: "all 0.15s",
      }}
    >
      Medium {show ? "ON ✓" : "OFF"}
    </button>
  );
}

// Shared EventRow — same as original, used in both Today and Week sections
function EventRow({ ev, i }) {
  const delta = ev.actual ? getSurpriseDelta(ev) : null;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "11px 0",
      borderTop: i > 0 ? `1px solid ${C.border}20` : "none",
      flexWrap: "wrap",
    }}>
      <div style={{ width: 80, fontSize: 11, color: C.sub, fontFamily: "monospace", flexShrink: 0 }}>
        {ev.time}
      </div>
      <div style={{ flex: 1, minWidth: 120 }}>
        <div style={{ fontSize: 13, color: C.text }}>{ev.event}</div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
          Fcst: <span style={{ color: C.sub }}>{ev.forecast}</span>
          {" · "}Prev: <span style={{ color: C.sub }}>{ev.previous}</span>
          {ev.actual && (
            <>
              {" · "}
              <span style={{ color: C.blue }}>Actual: {ev.actual}</span>
              {delta !== null && (
                <span style={{
                  color: surpriseColor(delta),
                  marginLeft: 4,
                  fontWeight: 700,
                }}>
                  ({fmtDelta(delta)})
                </span>
              )}
            </>
          )}
        </div>
      </div>
      <span style={{
        padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700,
        background: ev.impact === "high"   ? C.red    + "20"
                  : ev.impact === "medium" ? C.yellow + "20"
                  : C.blue + "15",
        color:      ev.impact === "high"   ? "#ff6b6b"
                  : ev.impact === "medium" ? C.yellow
                  : C.blue,
      }}>
        {ev.impact}
      </span>
    </div>
  );
}

// 3. Surprise Tracker card
function SurpriseTracker({ eventsByDay }) {
  // Flatten all events that have actual + numeric forecast
  const allEvents = Object.values(eventsByDay).flat();
  const released = allEvents
    .filter(ev => ev.actual && ev.actual !== "—" && ev.forecast && ev.forecast !== "—")
    .map(ev => ({ ...ev, delta: getSurpriseDelta(ev) }))
    .filter(ev => ev.delta !== null)
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

  if (released.length === 0) return null;

  return (
    <Card style={{ marginBottom: 16 }}>
      <SectionTitle action={
        <span style={{ fontSize: 11, color: C.muted, fontWeight: 400, fontStyle: "italic" }}>
          Actual vs forecast — largest miss first
        </span>
      }>
        Surprise Tracker
      </SectionTitle>
      {released.slice(0, 8).map((ev, i) => {
        const color = surpriseColor(ev.delta);
        const isLast = i === Math.min(released.length, 8) - 1;
        return (
          <div key={i} style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "10px 0",
            borderTop: i > 0 ? `1px solid ${C.border}20` : "none",
          }}>
            {/* Impact badge */}
            <span style={{
              padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700, flexShrink: 0,
              background: ev.impact === "high" ? C.red + "20" : ev.impact === "medium" ? C.yellow + "20" : C.border + "30",
              color:      ev.impact === "high" ? "#ff6b6b" : ev.impact === "medium" ? C.yellow : C.sub,
            }}>
              {ev.impact}
            </span>
            {/* Event name + meta */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {ev.event}
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                {ev.dateLabel} · Fcst: <span style={{ color: C.sub }}>{ev.forecast}</span>
                {" · "}Act: <span style={{ color: C.blue }}>{ev.actual}</span>
              </div>
            </div>
            {/* Delta */}
            <div style={{
              fontFamily: "monospace",
              fontWeight: 800,
              fontSize: 15,
              color,
              flexShrink: 0,
              minWidth: 52,
              textAlign: "right",
            }}>
              {fmtDelta(ev.delta)}
            </div>
          </div>
        );
      })}
    </Card>
  );
}

// 4. Impact Heatmap card
function ImpactHeatmap({ eventsByDay }) {
  const days = buildHeatmap(eventsByDay);
  const [hovered, setHovered] = useState(null);

  return (
    <Card style={{ marginBottom: 16 }}>
      <SectionTitle action={
        <span style={{ fontSize: 11, color: C.muted, fontWeight: 400, fontStyle: "italic" }}>
          7-day event density
        </span>
      }>
        Weekly Risk Heatmap
      </SectionTitle>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
        {days.map((day, i) => (
          <div
            key={i}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
            style={{
              background: heatCellBg(day),
              border: `1px solid ${heatCellBorder(day)}`,
              borderRadius: 8,
              padding: "10px 6px",
              textAlign: "center",
              cursor: "default",
              position: "relative",
              transition: "background 0.15s",
            }}
          >
            <div style={{ fontSize: 10, fontWeight: 700, color: day.isToday ? C.blue : C.sub, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {day.shortLabel}
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>
              {day.dateNum}
            </div>
            {/* Density pips */}
            <div style={{ display: "flex", justifyContent: "center", gap: 3, flexWrap: "wrap" }}>
              {day.high > 0 && (
                <span style={{ background: C.red + "30", color: "#ff6b6b", borderRadius: 4, fontSize: 10, fontWeight: 700, padding: "1px 5px" }}>
                  {day.high}H
                </span>
              )}
              {day.medium > 0 && (
                <span style={{ background: C.yellow + "25", color: C.yellow, borderRadius: 4, fontSize: 10, fontWeight: 700, padding: "1px 5px" }}>
                  {day.medium}M
                </span>
              )}
              {day.high === 0 && day.medium === 0 && (
                <span style={{ color: C.border, fontSize: 11 }}>—</span>
              )}
            </div>
            {/* Hover tooltip */}
            {hovered === i && (day.high > 0 || day.medium > 0 || day.low > 0) && (
              <div style={{
                position: "absolute",
                top: "calc(100% + 6px)",
                left: "50%",
                transform: "translateX(-50%)",
                background: "#12141f",
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                padding: "8px 10px",
                zIndex: 50,
                width: 160,
                textAlign: "left",
                boxShadow: "0 6px 20px rgba(0,0,0,0.5)",
                pointerEvents: "none",
              }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.sub, marginBottom: 4, textTransform: "uppercase" }}>
                  {day.label}
                </div>
                {(eventsByDay[day.label] || [])
                  .filter(ev => ev.impact === "high" || ev.impact === "medium")
                  .slice(0, 5)
                  .map((ev, j) => (
                    <div key={j} style={{ fontSize: 11, color: ev.impact === "high" ? "#ff6b6b" : C.yellow, marginBottom: 2, lineHeight: 1.4 }}>
                      {ev.time !== "All Day" ? ev.time + " " : ""}{ev.event}
                    </div>
                  ))
                }
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 14, marginTop: 10, justifyContent: "flex-end" }}>
        {[
          { color: "#ff6b6b", label: "High" },
          { color: C.yellow,  label: "Medium" },
        ].map(({ color, label }) => (
          <span key={label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: C.muted }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, display: "inline-block" }} />
            {label}
          </span>
        ))}
      </div>
    </Card>
  );
}

// ─── Feed status chip ─────────────────────────────────────────
//
// Replaces the old unconditional green "● Live". That chip printed "Live"
// whenever calLoading was false — including while the backend was serving a
// stale cache from a fetch that had been failing for hours.

function FeedStatusChip({ calLoading, calStatus, dataTrustworthy, lastUpdated }) {
  if (calLoading) {
    return <span style={{ fontSize: 11, color: C.yellow }}>⟳ Loading...</span>;
  }

  if (!dataTrustworthy) {
    const label =
      calStatus === "stale"             ? "▲ Stale data"      :
      calStatus === "uncovered"         ? "▲ Out of range"    :
      calStatus === "contract_mismatch" ? "▲ Service mismatch" :
                                          "▲ Unavailable";
    return <span style={{ fontSize: 11, color: C.red, fontWeight: 700 }}>{label}</span>;
  }

  const ageMin = lastUpdated
    ? Math.round((Date.now() - new Date(lastUpdated).getTime()) / 60000)
    : null;

  return (
    <span style={{ fontSize: 11, color: C.green }}>
      ● Live{ageMin !== null && ageMin > 0 ? ` · ${ageMin}m ago` : ""}
    </span>
  );
}

// ─── Main component ───────────────────────────────────────────

export function Market({ accounts = [] }) {
  const {
    eventsByDay, todayEvents, marketStatus, nextHighImpact, calLoading,
    calStatus, calError, dataTrustworthy, lastUpdated,
  } = useCalendar();
  const [showWeek,   setShowWeek]   = useState(false);
  const [showMedium, setShowMedium] = useState(false);

  const statusConfig = {
    safe:    { bg: "rgba(0,230,118,0.08)",  border: C.green,  icon: "✓", label: "SAFE TO TRADE", color: C.green,  desc: "No high-impact news in the next 45 minutes." },
    caution: { bg: "rgba(255,214,0,0.08)",  border: C.yellow, icon: "⚠", label: "CAUTION",        color: C.yellow, desc: nextHighImpact ? `${nextHighImpact.event} in ${nextHighImpact.minsUntil} min — reduce size.` : "High impact news approaching." },
    danger:  { bg: "rgba(255,23,68,0.10)",  border: C.red,    icon: "✕", label: "NO TRADE ZONE",  color: C.red,    desc: nextHighImpact ? `${nextHighImpact.event} in ${nextHighImpact.minsUntil} min — stay out.`  : "High impact news imminent." },
    // Data we can't vouch for never renders as green. "Unknown" is the honest
    // state when the feed is stale, uncovered, or down.
    unknown: { bg: "rgba(255,214,0,0.06)",  border: C.yellow, icon: "?", label: "STATUS UNKNOWN", color: C.yellow, desc: calError || "Calendar data unavailable — verify against an external calendar before trading." },
  };
  const sc = statusConfig[marketStatus] || statusConfig.unknown;

  // Filtered event lists based on medium toggle
  const visibleTodayEvents = showMedium
    ? todayEvents
    : todayEvents.filter(ev => ev.impact === "high");

  // High-impact events today for the Pre-Market Brief
  const highToday   = todayEvents.filter(ev => ev.impact === "high");
  const medToday    = todayEvents.filter(ev => ev.impact === "medium");
  const volWindows  = buildVolatilityWindows(highToday);

  return (
    <div>
      <h2 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 800 }}>Market Intelligence</h2>
      <p style={{ color: C.muted, fontSize: 12, marginBottom: 20 }}>Live economic calendar · Real-time risk signals</p>

      {/* ── Status Banner ── */}
      <div style={{
        background: sc.bg,
        border: `1px solid ${sc.border}25`,
        borderLeft: `3px solid ${sc.border}`,
        borderRadius: 12,
        padding: "14px 18px",
        marginBottom: 16,
        display: "flex",
        alignItems: "center",
        gap: 14,
        justifyContent: "space-between",
        flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 36, height: 36, borderRadius: "50%",
            background: sc.color + "20", border: `2px solid ${sc.color}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 16, color: sc.color, fontWeight: 700,
          }}>
            {sc.icon}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700 }}>
              MARKET STATUS: <span style={{ color: sc.color }}>{sc.label}</span>
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{sc.desc}</div>
          </div>
        </div>
        {/* Feature 1 — Live ticking countdown replaces static "Xm" chip */}
        <LiveCountdown nextHighImpact={nextHighImpact} color={sc.color} />
      </div>

      {/* ── Sentiment ── */}
      <div style={{ marginBottom: 16 }}>
        <MarketSentimentWidget />
      </div>

      {/* ── Feature 4 — Impact Heatmap ── */}
      {!calLoading && Object.keys(eventsByDay).length > 0 && (
        <ImpactHeatmap eventsByDay={eventsByDay} />
      )}

      {/* ── Today's Events ── */}
      <Card style={{ marginBottom: 16 }}>
        <SectionTitle action={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <MediumToggle show={showMedium} onToggle={() => setShowMedium(v => !v)} />
            <FeedStatusChip
              calLoading={calLoading}
              calStatus={calStatus}
              dataTrustworthy={dataTrustworthy}
              lastUpdated={lastUpdated}
            />
          </div>
        }>
          Today's Events — {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: "America/New_York" })}
        </SectionTitle>

        {calLoading ? (
          <div style={{ textAlign: "center", padding: 30, color: C.muted, fontSize: 13 }}>
            Fetching live calendar...
          </div>
        ) : !dataTrustworthy ? (
          // An empty list from an untrustworthy feed says nothing about the
          // calendar. Never render the green "clean day" affirmative here.
          <div style={{
            textAlign: "center", padding: 24, fontSize: 13,
            background: C.yellow + "08", border: `1px solid ${C.yellow}25`, borderRadius: 8,
          }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>⚠️</div>
            <div style={{ color: C.yellow, fontWeight: 700 }}>Calendar data unavailable</div>
            <div style={{ color: C.muted, marginTop: 6, maxWidth: 420, margin: "6px auto 0", lineHeight: 1.5, fontSize: 12 }}>
              {calError || "We can't confirm today's economic events right now."} This is <strong>not</strong> a clean calendar — check an external source before sizing up.
            </div>
          </div>
        ) : visibleTodayEvents.length === 0 ? (
          <div style={{ textAlign: "center", padding: 24, color: C.muted, fontSize: 13 }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>✅</div>
            {showMedium
              ? "No USD events scheduled today — clean day to trade."
              : todayEvents.length > 0
                ? <><span style={{ color: C.yellow }}>{todayEvents.length} medium/low event{todayEvents.length > 1 ? "s" : ""}</span> — turn on Medium to see them.</>
                : "No high-impact USD events scheduled today — clean day to trade."
            }
          </div>
        ) : (
          visibleTodayEvents.map((ev, i) => <EventRow key={i} ev={ev} i={i} />)
        )}
      </Card>

      {/* ── Feature 3 — Surprise Tracker ── */}
      {!calLoading && <SurpriseTracker eventsByDay={eventsByDay} />}

      {/* ── Full Week Toggle ── */}
      <Card style={{ marginBottom: 16 }}>
        <SectionTitle action={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {showWeek && <MediumToggle show={showMedium} onToggle={() => setShowMedium(v => !v)} />}
            <button
              onClick={() => setShowWeek(w => !w)}
              style={{
                background: "none", border: `1px solid ${C.border}`,
                borderRadius: 8, padding: "5px 12px",
                color: C.sub, cursor: "pointer", fontSize: 12,
              }}
            >
              {showWeek ? "Hide ↑" : "Show ↓"}
            </button>
          </div>
        }>
          Full Week Calendar
        </SectionTitle>

        {showWeek && (
          Object.entries(eventsByDay).map(([day, evs]) => {
            const filtered = showMedium ? evs : evs.filter(ev => ev.impact === "high");
            const todayLabel = new Date().toLocaleDateString("en-US", {
              weekday: "short", month: "short", day: "numeric", timeZone: "America/New_York",
            });
            const isToday = day === todayLabel;
            return (
              <div key={day} style={{ marginBottom: 16 }}>
                <div style={{
                  fontSize: 11, fontWeight: 700,
                  color: isToday ? C.green : C.sub,
                  textTransform: "uppercase", letterSpacing: "0.1em",
                  marginBottom: 4, paddingBottom: 6, borderBottom: `1px solid ${C.border}`,
                }}>
                  {day} {isToday ? "← Today" : ""}
                  {!showMedium && evs.length !== filtered.length && (
                    <span style={{ color: C.border, fontWeight: 400, marginLeft: 6, textTransform: "none", letterSpacing: 0 }}>
                      ({evs.length - filtered.length} medium/low hidden)
                    </span>
                  )}
                </div>
                {filtered.length === 0 ? (
                  <div style={{ fontSize: 12, color: C.muted, padding: "8px 0" }}>
                    No high-impact events{showMedium ? "" : " — turn on Medium to see others"}.
                  </div>
                ) : (
                  filtered.map((ev, i) => <EventRow key={i} ev={ev} i={i} />)
                )}
              </div>
            );
          })
        )}
      </Card>

      {/* ── Pre-Market Brief ── */}
      <Card style={{ marginBottom: 16 }}>
        <SectionTitle>Pre-Market Brief</SectionTitle>

        {/* Row 1 — High-impact summary (unchanged) */}
        <div style={{ display: "flex", gap: 12, marginBottom: 14, paddingBottom: 14, borderBottom: `1px solid ${C.border}` }}>
          <div style={{
            width: 38, height: 38, borderRadius: "50%",
            background: (highToday.length > 0 ? C.red : C.green) + "15",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, flexShrink: 0,
          }}>
            {!dataTrustworthy ? "⚠️" : marketStatus === "safe" ? "✅" : "🔔"}
          </div>
          <div>
            <div style={{
              fontSize: 13, fontWeight: 700,
              color: !dataTrustworthy ? C.yellow : highToday.length > 0 ? C.red : C.green,
            }}>
              {!dataTrustworthy
                ? "Can't confirm today's events"
                : highToday.length > 0
                  ? `${highToday.length} high-impact event${highToday.length > 1 ? "s" : ""} today`
                  : "No high-impact events today"}
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
              {!dataTrustworthy
                ? "Calendar feed is not reporting reliably — verify externally before trading."
                : highToday.length > 0
                  ? highToday.map(e => `${e.time} — ${e.event}`).join(" · ")
                  : "Clean calendar — good conditions to trade your setups."}
            </div>
          </div>
        </div>

        {/* Feature 5 — Dynamic volatility windows (replaces hardcoded copy) */}
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{
            width: 38, height: 38, borderRadius: "50%",
            background: C.green + "15",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, flexShrink: 0,
          }}>
            ⏰
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, color: C.green, fontWeight: 700 }}>
              {volWindows.length > 0 ? "Volatility windows today" : "Best trading windows"}
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
              {volWindows.length > 0 ? (
                <>
                  {volWindows.map((w, i) => (
                    <span key={i}>
                      <span style={{ color: C.sub }}>{w.window}</span>
                      {" "}
                      <span style={{ color: C.muted }}>({w.event})</span>
                      {i < volWindows.length - 1 ? " · " : ""}
                    </span>
                  ))}
                  {medToday.length > 0 && (
                    <span style={{ color: C.border }}> · {medToday.length} medium event{medToday.length > 1 ? "s" : ""} also today</span>
                  )}
                </>
              ) : (
                "9:30–11:30 AM ET · 1:00–3:30 PM ET · Avoid lunch chop 11:30–1:00"
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* ── Behavioral analytics ── */}
      <EventPerformancePanel accounts={accounts} />
    </div>
  );
}
